import { useState, useRef, useMemo, useEffect, useCallback } from 'react'
import { scaleTime } from 'd3-scale'
import { timeFormat } from 'd3-time-format'
import { PersonaIcon } from './PersonaIcon'
import { TimelineSegment, WorkerLifespan } from '../hooks/useMetrics'
import { Shard, ShardStatus } from '../types/shard'

interface TimelineProps {
  segments: TimelineSegment[]
  workerLifespans: Map<string, WorkerLifespan>
  shards: Shard[]
  onShardClick: (shard: Shard) => void
  onWorkerClick: (workerName: string) => void
}

const LANE_HEIGHT = 36
const LANE_GAP = 4
const HEADER_HEIGHT = 32
const SIDEBAR_WIDTH = 160

function statusColor(status: ShardStatus): string {
  switch (status) {
    case 'DONE': return '#94a3b8'
    case 'IN_PROGRESS': return '#14b8a6'
    case 'REVIEW': case 'FINAL_REVIEW': return '#8b5cf6'
    case 'BLOCKED': return '#ec4899'
    default: return '#fbbf24'
  }
}

const formatTime = timeFormat('%H:%M')
const formatDateTime = timeFormat('%b %d %H:%M')

export function Timeline({ segments, workerLifespans, shards, onShardClick, onWorkerClick }: TimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState(1)
  const [hoveredSegment, setHoveredSegment] = useState<TimelineSegment | null>(null)
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 })

  // Sort workers: Nexus first, then by spawn time
  const sortedWorkers = useMemo(() => {
    const workers = Array.from(workerLifespans.values())
    return workers.sort((a, b) => {
      if (a.persona === 'nexus') return -1
      if (b.persona === 'nexus') return 1
      return a.spawnTime - b.spawnTime
    })
  }, [workerLifespans])

  // Time domain - fit to segment data range only
  const { timeStart, timeEnd, hasActiveSegments } = useMemo(() => {
    if (segments.length === 0) {
      return { timeStart: Date.now() - 60000, timeEnd: Date.now(), hasActiveSegments: false }
    }

    const startTimes = segments.map(s => s.startTime)
    const endTimes = segments.filter(s => s.endTime).map(s => s.endTime!)
    const active = segments.some(s => s.endTime === null)

    const earliest = Math.min(...startTimes)
    const latest = endTimes.length > 0 ? Math.max(...endTimes) : earliest
    const end = active ? Date.now() : latest

    const span = end - earliest
    const padding = Math.max(span * 0.05, 60000)
    return {
      timeStart: earliest - padding,
      timeEnd: end + padding,
      hasActiveSegments: active,
    }
  }, [segments])

  // Only tick "now" if there are active segments
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (!hasActiveSegments) return
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [hasActiveSegments])

  const domainEnd = hasActiveSegments ? now : timeEnd

  // Fixed 20000px width that we KNOW renders bars correctly.
  // Zoom multiplies this. Cap at 60000 to prevent browser issues.
  const timelineWidth = Math.min(60000, Math.max(800, Math.round(20000 * zoom)))

  const xScale = scaleTime()
    .domain([new Date(timeStart), new Date(domainEnd)])
    .range([0, timelineWidth])

  const totalHeight = Math.max(200, sortedWorkers.length * (LANE_HEIGHT + LANE_GAP) + HEADER_HEIGHT)

  const tickCount = Math.max(4, Math.floor(timelineWidth / 120))
  const ticks = xScale.ticks(tickCount)
  const spanHours = (domainEnd - timeStart) / 3600000
  const tickFormatter = spanHours > 24 ? formatDateTime : formatTime

  // SPEC bands
  const specBands = useMemo(() => {
    const specs = shards.filter(s => s.id.startsWith('SPEC'))
    const specColors = ['#8b5cf6', '#06b6d4', '#f59e0b', '#ec4899', '#10b981', '#6366f1']

    return specs.map((spec, i) => {
      const specTasks = shards.filter(s => s.parent_id === spec.id).map(s => s.id)
      const specSegments = segments.filter(s => specTasks.includes(s.shardId))
      if (specSegments.length === 0) return null

      const start = Math.min(...specSegments.map(s => s.startTime))
      const end = specSegments.some(s => s.endTime === null)
        ? domainEnd
        : Math.max(...specSegments.filter(s => s.endTime).map(s => s.endTime!))

      return {
        id: spec.id,
        title: spec.title,
        x: xScale(new Date(start)),
        width: Math.max(xScale(new Date(end)) - xScale(new Date(start)), 2),
        color: specColors[i % specColors.length]!,
      }
    }).filter(Boolean) as { id: string; title: string; x: number; width: number; color: string }[]
  }, [shards, segments, xScale, domainEnd])

  // Sync vertical scroll between sidebar and timeline
  const handleTimelineScroll = useCallback(() => {
    if (containerRef.current && sidebarRef.current) {
      sidebarRef.current.scrollTop = containerRef.current.scrollTop
    }
  }, [])
  const handleSidebarScroll = useCallback(() => {
    if (containerRef.current && sidebarRef.current) {
      containerRef.current.scrollTop = sidebarRef.current.scrollTop
    }
  }, [])

  // Horizontal scroll via wheel (native, non-passive so preventDefault works)
  const wheelHandlerAttached = useRef(false)
  useEffect(() => {
    const el = containerRef.current
    if (!el || wheelHandlerAttached.current) return
    wheelHandlerAttached.current = true

    el.addEventListener('wheel', (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        const factor = e.deltaY > 0 ? 0.8 : 1.25
        setZoom(z => Math.max(0.25, Math.min(3, z * factor)))
      } else if (!e.shiftKey) {
        e.preventDefault()
        el.scrollLeft += e.deltaY
      }
    }, { passive: false })
  })

  // Auto-scroll to latest on first data load
  const hasScrolledRef = useRef(false)
  useEffect(() => {
    if (containerRef.current && segments.length > 0 && !hasScrolledRef.current) {
      hasScrolledRef.current = true
      containerRef.current.scrollLeft = containerRef.current.scrollWidth
    }
  }, [segments.length])

  if (sortedWorkers.length === 0 && segments.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-600 text-sm">
        No activity data yet
      </div>
    )
  }

  const scrollTo = (pos: 'start' | 'end') => {
    const el = containerRef.current
    if (!el) return
    el.scrollLeft = pos === 'start' ? 0 : el.scrollWidth - el.clientWidth
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Controls */}
      <div className="flex items-center gap-2 px-4 py-1.5 border-b border-cosmic-border/50 flex-shrink-0">
        <button onClick={() => setZoom(z => Math.max(0.25, z / 1.5))} className="text-xs text-gray-400 hover:text-white px-2 py-0.5 rounded bg-cosmic-surface">-</button>
        <span className="text-xs text-gray-500 w-12 text-center">{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom(z => Math.min(3, z * 1.5))} className="text-xs text-gray-400 hover:text-white px-2 py-0.5 rounded bg-cosmic-surface">+</button>
        <button onClick={() => setZoom(1)} className="text-xs text-gray-400 hover:text-white px-2 py-0.5 rounded bg-cosmic-surface">Reset</button>
        <div className="w-px h-4 bg-cosmic-border/50" />
        <button onClick={() => scrollTo('start')} className="text-xs text-gray-400 hover:text-white px-2 py-0.5 rounded bg-cosmic-surface">&larr; Start</button>
        <button onClick={() => scrollTo('end')} className="text-xs text-gray-400 hover:text-white px-2 py-0.5 rounded bg-cosmic-surface">Latest &rarr;</button>
        <span className="text-xs text-gray-600 ml-2">
          {segments.length} segments | {sortedWorkers.length} workers |
          {spanHours < 1 ? ` ${Math.round(spanHours * 60)}m` : spanHours < 24 ? ` ${spanHours.toFixed(1)}h` : ` ${(spanHours / 24).toFixed(1)}d`}
        </span>
      </div>

      {/* Timeline area */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Worker sidebar */}
        <div
          ref={sidebarRef}
          onScroll={handleSidebarScroll}
          className="flex-shrink-0 border-r border-cosmic-border overflow-y-auto overflow-x-hidden"
          style={{ width: SIDEBAR_WIDTH }}
        >
          <div style={{ height: HEADER_HEIGHT }} className="border-b border-cosmic-border/50" />
          {sortedWorkers.map(worker => (
            <button
              key={worker.name}
              onClick={() => onWorkerClick(worker.name)}
              className="flex items-center gap-2 px-3 w-full hover:bg-cosmic-surface/50 text-left"
              style={{ height: LANE_HEIGHT + LANE_GAP }}
            >
              <PersonaIcon persona={worker.persona} size="sm" />
              <span className="text-xs text-gray-300 truncate">{worker.name}</span>
            </button>
          ))}
        </div>

        {/* SVG timeline - min-w-0 lets flex item shrink below SVG intrinsic width */}
        <div
          ref={containerRef}
          onScroll={handleTimelineScroll}
          className="flex-1 min-w-0 overflow-auto"
        >
          <svg width={timelineWidth} height={totalHeight} style={{ display: 'block' }}>
            {/* SPEC bands */}
            {specBands.map(band => (
              <g key={band.id}>
                <rect x={band.x} y={0} width={band.width} height={totalHeight} fill={band.color} opacity={0.05} />
                <text x={band.x + 4} y={12} className="text-[9px]" fill={band.color} opacity={0.6}>{band.id}</text>
              </g>
            ))}

            {/* Time axis */}
            {ticks.map(tick => (
              <g key={tick.getTime()}>
                <line x1={xScale(tick)} y1={HEADER_HEIGHT} x2={xScale(tick)} y2={totalHeight} stroke="#2d2d3f" strokeWidth="0.5" />
                <text x={xScale(tick)} y={HEADER_HEIGHT - 8} textAnchor="middle" fill="#6b7280" fontSize="10">{tickFormatter(tick)}</text>
              </g>
            ))}

            {/* Now marker */}
            {hasActiveSegments && (
              <line x1={xScale(new Date(now))} y1={HEADER_HEIGHT} x2={xScale(new Date(now))} y2={totalHeight} stroke="#14b8a6" strokeWidth="1" strokeDasharray="4 2" />
            )}

            {/* Swim lanes */}
            {sortedWorkers.map((worker, laneIdx) => {
              const y = HEADER_HEIGHT + laneIdx * (LANE_HEIGHT + LANE_GAP) + LANE_GAP / 2
              const workerSegments = segments.filter(s => s.workerName === worker.name)

              return (
                <g key={worker.name}>
                  <rect x={0} y={y} width={timelineWidth} height={LANE_HEIGHT} fill={laneIdx % 2 === 0 ? '#131318' : '#1a1a24'} opacity={0.3} />

                  {workerSegments.map((seg, i) => {
                    const x1 = xScale(new Date(seg.startTime))
                    const x2 = xScale(new Date(seg.endTime || domainEnd))
                    const barWidth = Math.max(x2 - x1, 4)

                    return (
                      <g
                        key={`${seg.shardId}-${i}`}
                        onMouseEnter={(e) => { setHoveredSegment(seg); setTooltipPos({ x: e.clientX, y: e.clientY }) }}
                        onMouseLeave={() => setHoveredSegment(null)}
                        onClick={() => { const shard = shards.find(s => s.id === seg.shardId); if (shard) onShardClick(shard) }}
                        className="cursor-pointer"
                      >
                        <rect x={x1} y={y + 4} width={barWidth} height={LANE_HEIGHT - 8} rx={3} fill={statusColor(seg.status)} opacity={0.9} />
                        {barWidth > 50 && (
                          <text x={x1 + 6} y={y + LANE_HEIGHT / 2 + 3} fill="white" fontSize="10" fontFamily="monospace">{seg.shardId}</text>
                        )}
                      </g>
                    )
                  })}
                </g>
              )
            })}
          </svg>
        </div>
      </div>

      {/* Tooltip */}
      {hoveredSegment && (
        <div
          className="fixed z-50 bg-cosmic-depth border border-cosmic-border rounded-lg px-3 py-2 text-xs shadow-lg pointer-events-none"
          style={{ left: tooltipPos.x + 12, top: tooltipPos.y + 12 }}
        >
          <div className="font-mono text-white">{hoveredSegment.shardId}</div>
          <div className="text-gray-400">{hoveredSegment.shardTitle}</div>
          <div className="text-gray-500 mt-1">
            {new Date(hoveredSegment.startTime).toLocaleTimeString()} &rarr;{' '}
            {hoveredSegment.endTime ? new Date(hoveredSegment.endTime).toLocaleTimeString() : 'now'}
          </div>
        </div>
      )}
    </div>
  )
}
